import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiCheck, FiInfo, FiX } from 'react-icons/fi';
import styles from './XadomToast.module.scss';

interface ToastItem {
    id: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info' | 'default';
    duration?: number;
    timestamp: number;
    onClose?: () => void;
}

interface XadomToastProps {
    toasts: ToastItem[];
    onRemove: (id: string) => void;
}

export const XadomToast: React.FC<XadomToastProps> = ({ toasts, onRemove }) => {
    return (
        <div className={styles.toastContainer}>
            {toasts.map((toast, index) => (
                <ToastMessage 
                    key={toast.id} 
                    toast={toast} 
                    onRemove={onRemove}
                    zIndex={toasts.length - index}
                />
            ))}
        </div>
    );
};

const ToastMessage: React.FC<{ toast: ToastItem; onRemove: (id: string) => void; zIndex: number }> = ({ 
    toast, 
    onRemove,
    zIndex
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isHiding, setIsHiding] = useState(false);

    const getIcon = (type: string) => {
        switch (type) {
            case 'success': return <FiCheck />;
            case 'error': return <FiX />;
            case 'warning': return <FiAlertTriangle />;
            case 'info': return <FiInfo />;
            default: return <FiInfo />;
        }
    };

    useEffect(() => {
        // 애니메이션을 위해 약간의 딜레이
        const showTimer = setTimeout(() => setIsVisible(true), 10);
        
        // 자동 제거 (duration이 0이면 수동 닫기)
        let hideTimer: NodeJS.Timeout | null = null;
        
        if (toast.duration !== undefined && toast.duration > 0) {
            hideTimer = setTimeout(() => {
                setIsHiding(true);
                setTimeout(() => {
                    onRemove(toast.id)
                    toast.onClose?.();
                }, 250); // 애니메이션 후 제거 (0.25초)
            }, toast.duration);
        }

        return () => {
            clearTimeout(showTimer);
            if (hideTimer) clearTimeout(hideTimer);
        };
    }, [toast.id, toast.duration, onRemove]);

    const handleClick = () => {
        // 수동 닫기 (duration이 0인 경우에만)
        if (toast.duration === 0) {
            setIsHiding(true);
            setTimeout(() => {
                onRemove(toast.id)
                toast.onClose?.();
            }, 250);
        }
    };

    return (
        <div 
            className={`${styles.toast} ${styles[toast.type]} ${isVisible ? styles.visible : ''} ${isHiding ? styles.hiding : ''}`}
            onClick={handleClick}
            style={{ 
                cursor: (toast.duration === 0) ? 'pointer' : 'default',
                zIndex: zIndex
            }}
        >
            <div className={styles.toastContent}>
                <div className={styles.toastIcon}>
                    {getIcon(toast.type)}
                </div>
                <div className={styles.toastMessage}>
                    {toast.message}
                </div>
                {(toast.duration === 0) && (
                    <div className={styles.closeIcon}>✕</div>
                )}
            </div>
            {(toast.duration !== undefined && toast.duration > 0) && (
                <div className={styles.progressBar}>
                    <div 
                        className={styles.progressBarFill}
                        style={{ 
                            animationDuration: `${toast.duration}ms`
                        }}
                    />
                </div>
            )}
        </div>
    );
};